import React from "react";
import { Check, Clock, ShieldCheck, Factory } from "lucide-react";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import FinalCTA from "../components/site/FinalCTA";
import { COMMERCIAL_SERVICES, IMG } from "../lib/site";
import Seo, { serviceSchema } from "../components/site/Seo";
import Breadcrumbs from "../components/site/Breadcrumbs";

const PROMISES = [
  { icon: Clock, title: "On-Schedule Delivery", desc: "We work around your operations and hit deadlines so your business keeps moving." },
  { icon: ShieldCheck, title: "Code-Compliant Systems", desc: "Coatings that meet stringent structural and safety code requirements." },
  { icon: Factory, title: "Heavy-Duty Durability", desc: "Built for forklifts, foot traffic, chemicals and constant industrial use." },
];

const Commercial = () => (
  <div>
    <Seo
      title="Commercial & Industrial Floor Coatings | Northern Arizona | PRYME EPOXY"
      description="Heavy-duty commercial floor coatings, grind & seal and polished concrete for warehouses, shops and facilities in Prescott, Flagstaff and Sedona. Minimal downtime."
      path="/commercial"
      jsonLd={serviceSchema("Commercial Floor Coatings", "Commercial and industrial floor coatings in Northern Arizona.")}
    />
    <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Commercial", path: "/commercial" }]} />
    <PageHero
      overline="Commercial & Industrial"
      title="Floors That Keep Your Business Moving"
      subtitle="Grind & seal, polished concrete and industrial coating systems for warehouses, shops, showrooms and facilities across Northern Arizona."
      image={IMG.commercialWarehouse}
    />

    <section className="py-16 bg-zinc-950 border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-5 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {PROMISES.map((p) => (
          <div key={p.title} className="border border-zinc-800 bg-[#141414] p-7 flex items-start gap-4">
            <div className="w-11 h-11 shrink-0 bg-[#C69453]/10 border border-[#C69453]/30 flex items-center justify-center text-[#C69453]">
              <p.icon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">{p.title}</h3>
              <p className="text-zinc-400 text-sm mt-1">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="py-20 md:py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {COMMERCIAL_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 2) * 0.06}>
              <div className="border border-zinc-800 bg-[#141414] p-6 h-full">
                <h3 className="text-lg font-bold text-white">{s.title}</h3>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="space-y-5">
          <img src={IMG.commercialPaint} alt="Industrial warehouse floor coating" loading="lazy" className="w-full aspect-[4/3] object-cover border border-zinc-800" />
          <div className="bg-zinc-950 border border-zinc-800 p-6">
            <h3 className="text-xl font-semibold text-white">Why Facility Managers Choose PRYME</h3>
            <ul className="mt-4 space-y-2 text-zinc-300 text-sm">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C69453]" /> Dust-free, low-maintenance surfaces</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C69453]" /> Slip-resistant safety options</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C69453]" /> Minimal downtime installation</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C69453]" /> Honest, transparent quoting</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <Reveal>
          <p className="text-[#C69453] text-sm font-semibold tracking-widest uppercase">Recent Work</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mt-2">Recent Commercial Projects</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { src: IMG.commercialGloss, alt: "High-gloss commercial warehouse epoxy floor" },
            { src: IMG.commercialFlakeRoom1, alt: "Commercial flake floor coating with pass-through room" },
            { src: IMG.commercialFlakeRooms, alt: "Commercial flake floor across multiple rooms" },
            { src: IMG.commercialFlakeHall, alt: "Large commercial flake floor open space" },
          ].map((img, i) => (
            <Reveal key={img.src} delay={i * 0.06}>
              <img src={img.src} alt={img.alt} loading="lazy" className="w-full aspect-square object-cover border border-zinc-800" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <FinalCTA heading="Let's Talk About Your Commercial Project" />
  </div>
);

export default Commercial;
